import { ContractInsightsData } from '@/pages/ContractDetail';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard, CurrencyDisplay, PercentDisplay } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format, differenceInWeeks, parseISO } from 'date-fns';
import { Banknote, TrendingUp, Calendar, Wallet, Shield, Wrench, Clock, RotateCw } from 'lucide-react';
import PendingSignatureBanner from '@/components/agreements/PendingSignatureBanner';
import { visitsInView, type ContractInspections } from '@/lib/inspections';
import { activeAdvances } from '@/lib/advance-outstanding';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

interface Props {
    data: ContractInsightsData;
    /**
     * `get_contract_inspections`, the payload the Inspections section reads, so the tile counts
     * the same visits and prints the same Ghana date. Undefined while it loads or if it failed.
     */
    inspections?: ContractInspections | null;
    /** The inspections read failed; the tile says so and offers `onRetryInspections`. */
    inspectionsFailed?: boolean;
    onRetryInspections?: () => void;
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

const formatCompact = (amount: number) => {
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
    return amount.toFixed(0);
};

// Inline currency for text contexts (same subscript style)
const CurrencyInline = ({ amount }: { amount: number }) => (
    <>
        {new Intl.NumberFormat('en-GH', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount)}
        <span className="text-xs font-normal">GH₵</span>
    </>
);

export default function ContractInsightOverview({ data, inspections, inspectionsFailed, onRetryInspections }: Props) {
    const contract = data.contract;
    if (!contract) return null;
    const inspectionVisits = inspections ? visitsInView(inspections) : [];

    // THE THREE TOTALS ARE THE SERVER'S. get_contract_insights computes total_value,
    // total_paid and outstanding (clamped at 0) from the same rows this page reads, so the
    // formulas no longer live here. What stays below is the BREAKDOWN — the per-category
    // figures the donut and the allocation bars need, which are segments of those totals,
    // not second opinions about them.
    const totalContractValue = data.total_value;
    const totalPaid = data.total_paid;
    const outstanding = data.outstanding;

    // Value breakdown segments
    // Other loans (principal + interest)
    const loansPrincipal = (data.loans || []).reduce((sum, l) => sum + l.amount, 0);
    const loansInterest = (data.loans || []).reduce((sum, l) => sum + (l.amount * (l.interest_rate || 0) / 100), 0);

    // Insurance loans (principal = full loan amount, + interest on loan - deposit)
    const insuranceLoansPrincipal = (data.insurances || []).reduce((sum, i) => {
        if (!i.loan) return sum;
        return sum + i.loan.amount;
    }, 0);
    const insuranceLoansInterest = (data.insurances || []).reduce((sum, i) => {
        if (!i.loan) return sum;
        const netLoanAmount = i.loan.amount - (i.loan.deposit_total || 0);
        return sum + (netLoanAmount * (i.loan.interest_rate || 0) / 100);
    }, 0);

    // Overdue interest, already absolute: overdue_total is |total_overdue_interest|.
    const overdueInterestAmount = data.overdue_total;

    // Allocation breakdown — the bars' segments
    // deposit.amount is NET (credits − refunds), which is what counts as paid.
    const depositAmount = data.deposit?.amount || 0;
    const depositPaidTotal = data.deposit?.paid_total || 0;
    const depositRefundedTotal = data.deposit?.refunded_total || 0;
    const vehicleRepaid = data.allocation_summary?.contract || 0;
    const loanDirectPayments = (data.loans || []).reduce((sum, l) => sum + (l.repayments_total || 0), 0);
    const loansRepaid = (data.allocation_summary?.loans || 0) + loanDirectPayments;
    // Insurance repaid = deposits + direct repayments + allocations
    const insuranceDeposits = (data.insurances || []).reduce((sum, i) => {
        if (!i.loan) return sum;
        return sum + (i.loan.deposit_total || 0);
    }, 0);
    const insuranceDirectPayments = (data.insurances || []).reduce((sum, i) => {
        if (!i.loan) return sum;
        return sum + (i.loan.repayments_total || 0);
    }, 0);
    const insuranceRepaid = (data.allocation_summary?.insurance_loans || 0) + insuranceDirectPayments + insuranceDeposits;

    const progressPercent = totalContractValue > 0 ? (totalPaid / totalContractValue) * 100 : 0;

    // Get weekly rate from RPC (sum of contract, insurance loan, and loan instalments)
    const weeklyAmount = data.weekly_rate || 0;

    // Calculate weeks remaining (estimate based on weekly instalment amount)
    const weeksRemaining = weeklyAmount > 0 ? Math.ceil(outstanding / weeklyAmount) : 0;

    // Payment compliance (simplified - ratio of paid to expected by now)
    const weeksSinceSigned = differenceInWeeks(new Date(), new Date(contract.signed_at));
    const expectedPayments = weeksSinceSigned * weeklyAmount;
    const compliancePercent = expectedPayments > 0 ? Math.min(100, (totalPaid / expectedPayments) * 100) : 100;

    // Quick stats
    const repayments = data.repayments || [];
    const lastPayment = repayments[0];
    // What is still owed on each advance after the weekly split AND any direct repayment, the
    // Advances tab's money — see `activeAdvances`, whose one decision on the contract's total sets
    // the count and the figure alike: "None" only when the advances owe under half a pesewa between
    // them, which prints as 0.00. The interest is already in it, so nothing is added on top at the
    // render; `loansInterest` above stays the lifetime figure it prints.
    const { count: activeLoansCount, outstanding: activeLoansTotal } = activeAdvances(data.loans);
    const hasInsurance = (data.insurances || []).length > 0;
    const maintenances = data.maintenances || [];
    const lastMaintenance = maintenances[0];

    // ── Chart data ──
    const donutData = [
        { name: 'Vehicle', value: contract.sold_at, color: '#0f172a' }, // Slate 900
        { name: 'Insurance', value: insuranceLoansPrincipal + insuranceLoansInterest, color: '#cbd5e1' }, // Slate 300
        { name: 'Advances', value: loansPrincipal + loansInterest, color: '#475569' }, // Slate 600
        { name: 'Overdue Interest', value: overdueInterestAmount, color: '#94a3b8' }, // Slate 400
    ].filter(d => d.value > 0);

    const allocationItems = [
        { name: 'Vehicle', value: vehicleRepaid, color: '#0f172a' },
        { name: 'Deposit', value: depositAmount, color: '#cbd5e1' },
        { name: 'Insurance', value: insuranceRepaid, color: '#334155' },
        { name: 'Advances', value: loansRepaid, color: '#475569' },
    ].filter(d => d.value > 0);

    const CustomDonutTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const d = payload[0];
            const pct = ((d.value / totalContractValue) * 100).toFixed(1);
            return (
                <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
                    <p className="font-medium">{d.name}</p>
                    <p className="text-muted-foreground">{formatCurrency(d.value)} ({pct}%)</p>
                </div>
            );
        }
        return null;
    };

    const CustomAllocationTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const d = payload[0].payload;
            const pct = totalPaid > 0 ? ((d.value / totalPaid) * 100).toFixed(1) : '0';
            return (
                <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
                    <p className="font-medium">{d.name}</p>
                    <p className="text-muted-foreground">{formatCurrency(d.value)} ({pct}%)</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-6">
            {/* An agreement waiting for this driver's signature is the only time-sensitive,
                actionable item on the overview, so it sits above the figures. Renders nothing
                when there is nothing to sign. */}
            <PendingSignatureBanner />

            {/* KPI Cards */}
            <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
                <StatCard
                    title="Total Paid"
                    value={<CurrencyDisplay amount={totalPaid} />}
                    description="The total amount paid across all categories including vehicle payments, deposits, insurance premiums — advanced or paid in full — and operational expense advances."
                />
                <StatCard
                    title="Outstanding"
                    value={<CurrencyDisplay amount={outstanding} />}
                    description="The remaining balance to be paid, calculated as total contract value minus all payments received."
                />
                <StatCard
                    title="Weekly Rate"
                    value={<CurrencyDisplay amount={weeklyAmount} />}
                    description="The current weekly instalment amount due, combining contract instalments and any active advance repayments."
                />
                <StatCard
                    title="Weeks Left"
                    value={weeksRemaining}
                    description="Estimated number of weeks to complete payment, based on the outstanding balance and current weekly rate."
                />
                <StatCard
                    title="Compliance"
                    value={<PercentDisplay value={compliancePercent} />}
                    description="Payment compliance ratio comparing actual payments received against expected payments based on weeks since signing."
                />
            </div>

            {/* ── Financial Charts Grid ── */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* ── Value Breakdown – Donut Chart ── */}
                <Card className="border border-border/40 flex flex-col">
                    <CardHeader className="pb-2 flex-none">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-medium">Value Breakdown</CardTitle>
                            <span className="text-sm font-semibold"><CurrencyInline amount={totalContractValue} /></span>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col gap-6">
                        {/* Donut */}
                        <div className="relative w-[180px] h-[180px] mx-auto flex-shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={donutData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={2}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {donutData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomDonutTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Center label */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="text-xs text-muted-foreground">Total</span>
                                <span className="text-sm font-bold">{formatCompact(totalContractValue)}</span>
                            </div>
                        </div>

                        {/* Legend list */}
                        <div className="w-full space-y-3 mt-auto">
                            {donutData.map((item) => {
                                const pct = ((item.value / totalContractValue) * 100).toFixed(1);
                                return (
                                    <div key={item.name} className="flex items-center gap-3">
                                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                                        <span className="text-sm text-muted-foreground flex-1">{item.name}</span>
                                        <span className="text-sm font-medium tabular-nums"><CurrencyInline amount={item.value} /></span>
                                        <span className="text-xs text-muted-foreground w-12 text-right tabular-nums">{pct}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* ── Payment Allocation – Bar Chart ── */}
                {totalPaid > 0 && (
                    <Card className="border border-border/40 flex flex-col">
                        <CardHeader className="pb-2 flex-none">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base font-medium flex items-center gap-2">
                                    <Wallet className="h-4 w-4" />
                                    Payment Allocation
                                </CardTitle>
                                <span className="text-sm font-semibold"><CurrencyInline amount={totalPaid} /></span>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col gap-6">
                            {/* Bar Chart */}
                            <div className="relative w-full h-[180px] flex-shrink-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={allocationItems} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.2)" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                                        <YAxis tickFormatter={(val) => formatCompact(val)} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                                        <Tooltip content={<CustomAllocationTooltip />} cursor={{ fill: 'hsl(var(--muted)/0.5)' }} />
                                        <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={40}>
                                            {allocationItems.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Legend */}
                            <div className="w-full space-y-3 mt-auto">
                                {allocationItems.map((item) => {
                                    const pct = totalPaid > 0 ? ((item.value / totalPaid) * 100).toFixed(1) : '0';
                                    return (
                                        <div key={item.name} className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                                            <span className="text-sm text-muted-foreground flex-1">{item.name}</span>
                                            <span className="text-sm font-medium tabular-nums"><CurrencyInline amount={item.value} /></span>
                                            <span className="text-xs text-muted-foreground w-12 text-right tabular-nums">{pct}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* ── Payment Progress – Linear Progress ── */}
                <Card className="border border-border/40 flex flex-col">
                    <CardHeader className="pb-2 flex-none">
                        <CardTitle className="text-base font-medium flex items-center gap-2">
                            <TrendingUp className="h-4 w-4" />
                            Payment Progress
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col gap-6 pt-4">
                        {/* Big Stats & Progress Bar */}
                        <div className="flex-1 flex flex-col justify-center w-full space-y-8">
                            <div className="text-center space-y-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Overall Completion</span>
                                <div className="text-5xl font-bold tracking-tighter text-primary">
                                    {progressPercent.toFixed(1)}%
                                </div>
                            </div>
                            
                            <div className="space-y-2 w-full px-2">
                                <div className="h-4 w-full bg-muted rounded-full overflow-hidden shadow-inner">
                                    <div 
                                        className="h-full rounded-full transition-all duration-1000 ease-out border border-border" 
                                        style={{ 
                                            width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                                            backgroundColor: 'hsl(var(--foreground))'
                                        }} 
                                    />
                                </div>
                                <div className="flex justify-between text-xs font-medium text-muted-foreground pt-1">
                                    <span>GH₵0</span>
                                    <span>{formatCompact(totalContractValue)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Stats list */}
                        <div className="w-full space-y-3 mt-auto">
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full flex-shrink-0 bg-foreground" />
                                <span className="text-sm text-muted-foreground flex-1">Total Paid</span>
                                <span className="text-sm font-medium tabular-nums"><CurrencyInline amount={totalPaid} /></span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full flex-shrink-0 bg-orange-500" />
                                <span className="text-sm text-muted-foreground flex-1">Outstanding</span>
                                <span className="text-sm font-medium tabular-nums text-orange-600"><CurrencyInline amount={outstanding} /></span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#94a3b8' }} />
                                <span className="text-sm text-muted-foreground flex-1">Contract Value</span>
                                <span className="text-sm font-medium tabular-nums"><CurrencyInline amount={totalContractValue} /></span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>


            {/* Quick Stats Grid */}
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {/* Deposit */}
                <Card className="border border-border/40">
                    <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                            <div className="space-y-1">
                                <p className="text-sm text-muted-foreground flex items-center gap-2">
                                    <Wallet className="h-4 w-4" />
                                    {depositRefundedTotal > 0 ? 'Net Deposit' : 'Deposit'}
                                </p>
                                <p className="text-xl font-semibold">
                                    {data.deposit?.amount != null ? (
                                        <CurrencyInline amount={data.deposit.amount} />
                                    ) : 'None'}
                                </p>
                                {depositRefundedTotal > 0 && (
                                    <p className="text-xs text-muted-foreground tabular-nums">
                                        <CurrencyInline amount={depositPaidTotal} /> paid ·{' '}
                                        <CurrencyInline amount={depositRefundedTotal} /> refunded
                                    </p>
                                )}
                            </div>
                            {(data.deposit?.count || 0) > 0 && (
                                <Badge variant="outline">
                                    {data.deposit?.count} payment{data.deposit?.count !== 1 ? 's' : ''}
                                </Badge>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Active Loans */}
                <Card className="border border-border/40">
                    <CardContent className="pt-6">
                        <div className="space-y-1">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                <Banknote className="h-4 w-4" />
                                Active Advances
                            </p>
                            <p className="text-xl font-semibold">
                                {activeLoansCount > 0 ? (
                                    <>
                                        {activeLoansCount} (<CurrencyInline amount={activeLoansTotal} /> outstanding)
                                    </>
                                ) : (
                                    'None'
                                )}
                            </p>
                            {activeLoansCount > 0 && (
                                <p className="text-xs text-muted-foreground">
                                    Principal: <CurrencyInline amount={loansPrincipal} /> + Interest: <CurrencyInline amount={loansInterest} />
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Insurance */}
                <Card className="border border-border/40">
                    <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                            <div className="space-y-1">
                                <p className="text-sm text-muted-foreground flex items-center gap-2">
                                    <Shield className="h-4 w-4" />
                                    Insurance
                                </p>
                                <p className="text-xl font-semibold">
                                    {hasInsurance ? `${data.insurances.length} policy` : 'None'}
                                </p>
                            </div>
                            {hasInsurance && data.insurances[0]?.starts_at && (
                                <span className="text-xs text-muted-foreground">
                                    Started {format(new Date(data.insurances[0].starts_at), 'MMM yyyy')}
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Last Maintenance */}
                <Card className="border border-border/40">
                    <CardContent className="pt-6">
                        <div className="space-y-1">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                <Wrench className="h-4 w-4" />
                                Last Maintenance
                            </p>
                            <p className="text-xl font-semibold">
                                {lastMaintenance ? (
                                    format(new Date(lastMaintenance.started_at), 'MMM d, yyyy')
                                ) : (
                                    'None'
                                )}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Last Payment */}
                <Card className="border border-border/40">
                    <CardContent className="pt-6">
                        <div className="space-y-1">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Last Payment
                            </p>
                            <p className="text-xl font-semibold">
                                {lastPayment ? (
                                    <>
                                        {format(new Date(lastPayment.paid_at), 'MMM d, yyyy')}
                                        <span className="text-sm font-normal text-muted-foreground ml-2">
                                            (<CurrencyInline amount={lastPayment.amount} />)
                                        </span>
                                    </>
                                ) : (
                                    'No payments yet'
                                )}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Inspections */}
                <Card className="border border-border/40">
                    <CardContent className="pt-6">
                        <div className="space-y-1">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Inspections
                            </p>
                            <p className="text-xl font-semibold">
                                {inspectionsFailed ? (
                                    <span className="flex flex-wrap items-center gap-2 text-sm font-normal text-muted-foreground">
                                        Could not load
                                        {onRetryInspections && (
                                            <Button variant="outline" size="sm" onClick={onRetryInspections} className="gap-1.5">
                                                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                                                Try again
                                            </Button>
                                        )}
                                    </span>
                                ) : !inspections ? (
                                    '—'
                                ) : inspectionVisits.length > 0 ? (
                                    <>
                                        {inspectionVisits.length} total
                                        <span className="text-sm font-normal text-muted-foreground ml-2">
                                            {/* A Ghana calendar date, read as one (never new Date(ymd)), in the grid's MMM d order. */}
                                            (last: {format(parseISO(inspectionVisits[0].inspected_on), 'MMM d')})
                                        </span>
                                    </>
                                ) : (
                                    'None'
                                )}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
